import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { getStatusCodeFromError } from 'src/utils/error';
import { useAppSelector } from 'src/redux/hooks';
import { selectLearningModuleExists } from 'src/redux/slices/learningModules';
import type { AppState } from 'src/redux/store';
import { useLearningModule } from '../../hooks';
import LearningModuleContentView from '../../components/learning-module/LearningModuleContentView';
import { ParentEntityProvider } from '../../contexts/ParentEntityContext';
import { EntityType } from '../../constants/entity';
import { useMetrics } from '../../metrics/useUserMetrics';
import { usePagePerformance } from '../../metrics/usePagePerformance';
import { FeatureName } from '../../metrics/metricsFields';

const LearningModulePage = () => {
  const { learningModuleId } = useParams();
  const location = useLocation();
  const { viewMetric } = useMetrics();

  if (!learningModuleId) {
    const errorMessage = `Expected learningModuleId to be present in browser URL path: ${location.pathname}`;
    const error = new Error(errorMessage);
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      path: location.pathname,
      url: window.location.href,
    });
    throw error;
  }

  const {
    learningModule,
    isLoading: learningModuleLoading,
    isError: learningModuleError,
    error: learningModuleErrorObj,
  } = useLearningModule(learningModuleId);

  const moduleExistsInStore = useAppSelector(
    (state: AppState) => selectLearningModuleExists(state, learningModuleId),
  );

  useEffect(() => {
    if (learningModuleError && !learningModuleLoading) {
      const status = getStatusCodeFromError(learningModuleErrorObj);
      if (status && status >= 400 && status < 500 && moduleExistsInStore) {
        ClientLogger.error(ClientLog.ModuleRenderFailure, {
          message: 'Previously rendered module returned 4xx on GetModule',
          moduleId: learningModuleId,
          statusCode: status,
          source: 'module-page',
          url: window.location.href,
        });
      }
    }
  }, [learningModuleError, learningModuleLoading, learningModuleErrorObj, moduleExistsInStore, learningModuleId]);

  // Track per-page performance (latency + availability) once the module finishes loading.
  // learningModuleId is the resource key so client-side navigation between modules (which reuses
  // this component instance) resets the measurement and re-emits metrics for each module.
  usePagePerformance(
    FeatureName.LearningModulePage,
    learningModuleLoading,
    learningModuleError,
    learningModuleId,
  );

  // Use useEffect to track page view after component mounts
  useEffect(() => {
    if (!learningModuleLoading && !learningModuleError) {
      viewMetric({
        feature: FeatureName.LearningModulePage,
        metadata: {
          url: window.location.href,
          moduleId: learningModuleId,
        },
      });
    }
  }, [learningModuleLoading, learningModuleError, viewMetric, learningModuleId]);

  if (learningModuleLoading) return <LoadingView />;

  if (learningModuleError) {
    const statusCode = getStatusCodeFromError(learningModuleErrorObj);
    const isClientError = statusCode !== undefined && statusCode >= 400 && statusCode < 500;

    const logContext = {
      learningModuleId,
      statusCode,
      path: location.pathname,
      url: window.location.href,
    };

    if (isClientError) {
      // 4xx errors (e.g., 404 module not found) are expected client-side
      // scenarios — API errors already classified in baseQuery.ts
      ClientLogger.warn(
        ClientLog.IllegalInternalDataState,
        new Error('Resource not found or access restricted for learning module'),
        logContext,
      );
    } else {
      ClientLogger.error(
        ClientLog.IllegalInternalDataState,
        new Error('Failed to load learning module'),
        logContext,
      );
    }

    return <ErrorView statusCode={statusCode} />;
  }

  if (!learningModule) {
    ClientLogger.error(
      ClientLog.IllegalInternalDataState,
      new Error('Learning module not found or empty response'),
      {
        learningModuleId,
        path: location.pathname,
        url: window.location.href,
      },
    );
    return <ErrorView statusCode={getStatusCodeFromError(learningModuleErrorObj)} />;
  }

  return (
    <ParentEntityProvider parentId={learningModuleId} parentType={EntityType.ModulePage}>
      <div className="flex justify-center px-su-space-08 pt-[24px]">
        <div className="mx-auto flex w-full flex-col">
          <h2
            slot="title"
            className="mb-[16px] text-wrap text-left"
          >
            {learningModule.title}
          </h2>
          <LearningModuleContentView learningModule={learningModule} />
        </div>
      </div>
    </ParentEntityProvider>
  );
};

export default LearningModulePage;
